import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import axios from 'axios';
import { 
  Truck, 
  Shield,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Loader2,
  FileText,
  Building2,
  Phone,
  Mail,
  Calendar,
  ExternalLink,
  Gift,
  Zap,
  DollarSign,
  Percent,
  History,
  Save,
  Briefcase,
  Trash2,
  Ban,
  MapPin,
  Package,
  TrendingUp,
  TrendingDown,
  UserX,
  CreditCard,
  AlertTriangle,
  MessageSquare,
  HelpCircle,
  Star,
  BadgeCheck,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-yellow-500/15 text-yellow-600',
  under_review: 'bg-blue-500/15 text-blue-500',
  verified: 'bg-green-500/15 text-green-500',
  rejected: 'bg-red-500/15 text-red-500'
};

const jobStatusColors = {
  posted: 'bg-blue-500/15 text-blue-500',
  quoted: 'bg-yellow-500/15 text-yellow-600',
  accepted: 'bg-purple-500/15 text-purple-500',
  in_progress: 'bg-orange-500/15 text-orange-500',
  picked_up: 'bg-orange-500/15 text-orange-500',
  completed: 'bg-green-500/15 text-green-500',
  cancelled: 'bg-red-500/15 text-red-500',
  disputed: 'bg-red-500/15 text-red-500'
};

const ProviderCard = ({ provider, onView, onGrantTrial, onDelete }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-card border border-border rounded-sm p-4 hover:border-primary/30 transition-colors"
  >
    <div className="flex items-start justify-between mb-3">
      <div>
        <h3 className="font-semibold">{provider.full_name}</h3>
        <p className="text-sm text-muted-foreground">{provider.company_name || 'No company name'}</p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <Badge className={statusColors[provider.verification_status] || statusColors.pending}>
          {provider.verification_status || 'pending'}
        </Badge>
        {provider.free_trial_active && (
          <Badge className="bg-purple-500/15 text-purple-500">
            <Gift className="w-3 h-3 mr-1" />
            Trial Active
          </Badge>
        )}
        {provider.weekly_subscription_active && (
          <Badge className="bg-green-500/15 text-green-500">
            <Zap className="w-3 h-3 mr-1" />
            Subscribed
          </Badge>
        )}
      </div>
    </div>
    
    <div className="space-y-1 text-sm text-muted-foreground mb-4">
      <div className="flex items-center gap-2">
        <Mail className="w-3.5 h-3.5" />
        {provider.email}
      </div>
      <div className="flex items-center gap-2">
        <Phone className="w-3.5 h-3.5" />
        {provider.phone}
      </div>
      {provider.abn && (
        <div className="flex items-center gap-2">
          <Building2 className="w-3.5 h-3.5" />
          ABN: {provider.abn}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Calendar className="w-3.5 h-3.5" />
        Joined: {new Date(provider.created_at).toLocaleDateString('en-AU')}
      </div>
    </div>
    
    <div className="flex gap-2">
      <Button 
        variant="outline" 
        size="sm" 
        className="flex-1 gap-2"
        onClick={() => onView(provider)}
        data-testid={`view-provider-${provider.id}`}
      >
        <Eye className="w-4 h-4" />
        Review
      </Button>
      {!provider.weekly_subscription_active && !provider.free_trial_active && (
        <Button 
          variant="outline" 
          size="sm" 
          className="flex-1 gap-2 text-purple-500 border-purple-500/30 hover:bg-purple-500/10"
          onClick={() => onGrantTrial(provider)}
          data-testid={`grant-trial-${provider.id}`}
        >
          <Gift className="w-4 h-4" />
          Grant Trial
        </Button>
      )}
      <Button 
        variant="outline" 
        size="sm" 
        className="gap-2 text-red-500 border-red-500/30 hover:bg-red-500/10"
        onClick={() => onDelete(provider)}
        data-testid={`delete-provider-${provider.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  </motion.div>
);

export default function AdminPage() {
  const [adminToken, setAdminToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [trialDays, setTrialDays] = useState('7');
  const [actionNotes, setActionNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Platform fee state
  const [platformFee, setPlatformFee] = useState(10);
  const [newPlatformFee, setNewPlatformFee] = useState(10);
  const [feeHistory, setFeeHistory] = useState([]);
  const [updatingFee, setUpdatingFee] = useState(false);
  
  // Job management state
  const [jobs, setJobs] = useState([]);
  const [jobStatusFilter, setJobStatusFilter] = useState('all');
  const [showOldJobs, setShowOldJobs] = useState(false);
  const [cancelJobDialog, setCancelJobDialog] = useState({ open: false, job: null });
  const [cancelReason, setCancelReason] = useState('');
  const [cleaningUp, setCleaningUp] = useState(false);
  
  // Job detail modal state (for viewing full job info, quotes, Q&A)
  const [jobDetailDialog, setJobDetailDialog] = useState({ open: false, job: null });
  const [jobQuotes, setJobQuotes] = useState([]);
  const [jobQuestions, setJobQuestions] = useState([]);
  const [jobMessages, setJobMessages] = useState([]);
  const [loadingJobDetails, setLoadingJobDetails] = useState(false);

  // Registration analytics state
  const [registrationStats, setRegistrationStats] = useState(null);
  const [incompleteRegistrations, setIncompleteRegistrations] = useState([]);

  // Delete user state
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, user: null });
  const [deletingUser, setDeletingUser] = useState(false);

  // Banned users state
  const [bannedUsers, setBannedUsers] = useState([]);
  const [unbanningUser, setUnbanningUser] = useState(null);

  // Content moderation state
  const [flaggedContent, setFlaggedContent] = useState([]);
  const [processingModeration, setProcessingModeration] = useState(null);

  // Database reset state
  const [resetDialog, setResetDialog] = useState({ open: false });
  const [resettingData, setResettingData] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');

  // Failed payouts state
  const [failedPayouts, setFailedPayouts] = useState([]);
  const [retryingPayout, setRetryingPayout] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);

  useEffect(() => {
    // No auto-login - require password entry
  }, []);

  // Fetch job details (quotes, Q&A, messages) for admin view
  const fetchJobDetails = async (job) => {
    setLoadingJobDetails(true);
    setJobDetailDialog({ open: true, job });
    try {
      const response = await axios.get(`${API_URL}/api/admin/job/${job.id}/details`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = response.data;
      setJobDetailDialog({ open: true, job: data.job });
      setJobQuotes(data.quotes || []);
      setJobQuestions(data.questions || []);
      setJobMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoadingJobDetails(false);
    }
  };

  const verifyAndFetch = async () => {
    if (!adminToken) {
      toast.error('Please enter the admin password');
      return;
    }
    
    setLoading(true);
    try {
      const [pendingRes, allRes, feeRes, jobsRes, statsRes, incompleteRes, bannedRes, flaggedRes, payoutsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/providers/pending`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API_URL}/api/admin/providers/all`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API_URL}/api/admin/settings/platform-fee`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API_URL}/api/admin/jobs`, {
          headers: { Authorization: `Bearer ${adminToken}` },
          params: { include_old: showOldJobs }
        }),
        axios.get(`${API_URL}/api/admin/stats/registrations`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API_URL}/api/admin/registrations/incomplete`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API_URL}/api/admin/banned-users`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API_URL}/api/admin/moderation/flagged`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${API_URL}/api/admin/payouts/failed`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }).catch(() => ({ data: { failed_payouts: [] } }))
      ]);
      
      setPendingProviders(pendingRes.data.providers);
      setProviders(allRes.data.providers);
      setPlatformFee(feeRes.data.current_fee_percent);
      setNewPlatformFee(feeRes.data.current_fee_percent);
      setFeeHistory(feeRes.data.history || []);
      setJobs(jobsRes.data.jobs || []);
      setRegistrationStats(statsRes.data);
      setIncompleteRegistrations(incompleteRes.data.registrations || []);
      setBannedUsers(bannedRes.data.banned_users || []);
      setFlaggedContent(flaggedRes.data.flagged_items || []);
      setFailedPayouts(payoutsRes.data.failed_payouts || []);
      setIsAuthenticated(true);
      toast.success('Admin access granted');
    } catch (error) {
      console.error('Admin auth error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Invalid admin password');
      } else {
        toast.error('Failed to authenticate. Please try again.');
      }
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Content moderation handlers
  const handleApproveQuestion = async (questionId) => {
    setProcessingModeration(questionId);
    try {
      await axios.post(`${API_URL}/api/admin/moderation/question/${questionId}/approve`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Question approved and now visible');
      setFlaggedContent(prev => prev.filter(q => q.id !== questionId));
    } catch (error) {
      toast.error('Failed to approve question');
    } finally {
      setProcessingModeration(null);
    }
  };

  const handleApproveAnswer = async (questionId) => {
    setProcessingModeration(`answer-${questionId}`);
    try {
      await axios.post(`${API_URL}/api/admin/moderation/answer/${questionId}/approve`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Answer approved and now visible');
      setFlaggedContent(prev => prev.map(q => 
        q.id === questionId ? { ...q, answer_moderation_status: 'approved' } : q
      ).filter(q => q.moderation_status !== 'approved' || q.answer_moderation_status === 'pending_review'));
    } catch (error) {
      toast.error('Failed to approve answer');
    } finally {
      setProcessingModeration(null);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    setProcessingModeration(questionId);
    try {
      await axios.delete(`${API_URL}/api/admin/moderation/question/${questionId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Question deleted');
      setFlaggedContent(prev => prev.filter(q => q.id !== questionId));
    } catch (error) {
      toast.error('Failed to delete question');
    } finally {
      setProcessingModeration(null);
    }
  };

  const handleDeleteAnswer = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this answer?')) return;
    setProcessingModeration(`answer-${questionId}`);
    try {
      await axios.delete(`${API_URL}/api/admin/moderation/answer/${questionId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Answer deleted');
      setFlaggedContent(prev => prev.map(q => 
        q.id === questionId ? { ...q, answer: null, answer_moderation_status: null } : q
      ));
    } catch (error) {
      toast.error('Failed to delete answer');
    } finally {
      setProcessingModeration(null);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    verifyAndFetch();
  };

  const handleViewProvider = async (provider) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/provider/${provider.id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedProvider(response.data);
      setActionNotes('');
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load provider details');
    }
  };

  const handleVerifyAction = async (action) => {
    if (!selectedProvider) return;
    
    setProcessing(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/provider/${selectedProvider.id}/verify`,
        { action, notes: actionNotes },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(`Provider ${action === 'approve' ? 'verified' : 'rejected'} successfully`);
      setDetailDialogOpen(false);
      verifyAndFetch();
    } catch (error) {
      toast.error('Failed to update provider status');
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenTrialDialog = (provider) => {
    setSelectedProvider(provider);
    setTrialDays('7');
    setTrialDialogOpen(true);
  };

  const handleGrantTrial = async () => {
    if (!selectedProvider) return;
    
    setProcessing(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/provider/${selectedProvider.id}/grant-trial?trial_days=${trialDays}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(`${trialDays}-day free trial granted to ${selectedProvider.full_name}`);
      setTrialDialogOpen(false);
      verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant trial');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserDialog.user) return;
    
    setDeletingUser(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/user/${deleteUserDialog.user.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(`User ${deleteUserDialog.user.email} has been deleted and banned`);
      setDeleteUserDialog({ open: false, user: null });
      verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleUnbanUser = async (bannedUser) => {
    setUnbanningUser(bannedUser.id);
    try {
      await axios.delete(
        `${API_URL}/api/admin/banned-user/${bannedUser.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(`${bannedUser.email} has been unbanned`);
      verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    } finally {
      setUnbanningUser(null);
    }
  };

  const handleUpdatePlatformFee = async () => {
    setUpdatingFee(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/settings/platform-fee`,
        { fee_percent: newPlatformFee },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      toast.success(response.data.message);
      setPlatformFee(newPlatformFee);
      verifyAndFetch(); // Refresh to get updated history
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update platform fee');
    } finally {
      setUpdatingFee(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!confirm('Are you sure you want to permanently delete this job? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/job/${jobId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Job deleted successfully');
      verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    }
  };

  const handleCancelJob = async () => {
    if (!cancelJobDialog.job) return;
    
    try {
      await axios.post(
        `${API_URL}/api/admin/job/${cancelJobDialog.job.id}/cancel`,
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('Job cancelled successfully');
      setCancelJobDialog({ open: false, job: null });
      setCancelReason('');
      verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel job');
    }
  };

  const handleCleanupExpiredJobs = async () => {
    setCleaningUp(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/jobs/cleanup-expired?days_old=30`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(response.data.message);
      verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup jobs');
    } finally {
      setCleaningUp(false);
    }
  };

  // Retry failed payout for a single job
  const handleRetryPayout = async (jobId) => {
    setRetryingPayout(jobId);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/payouts/retry/${jobId}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(response.data.message);
      verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry payout');
    } finally {
      setRetryingPayout(null);
    }
  };

  // Retry all failed payouts
  const handleRetryAllPayouts = async () => {
    setRetryingAll(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/payouts/retry-all`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const { results } = response.data;
      if (results.success.length > 0) {
        toast.success(`${results.success.length} payout(s) sent successfully!`);
      }
      if (results.failed.length > 0) {
        toast.error(`${results.failed.length} payout(s) failed`);
      }
      verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry payouts');
    } finally {
      setRetryingAll(false);
    }
  };

  const handleResetAllData = async () => {
    if (resetConfirmText !== 'DELETE ALL') {
      toast.error('Please type DELETE ALL to confirm');
      return;
    }
    
    setResettingData(true);
    try {
      const res = await adminApi.resetAllData();
      const deleted = res.data.deleted;
      toast.success(`Database reset! Deleted: ${deleted.users} users, ${deleted.jobs} jobs, ${deleted.quotes} quotes`);
      setResetDialog({ open: false });
      setResetConfirmText('');
      // Refresh the data
      await verifyAndFetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset database');
    } finally {
      setResettingData(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (jobStatusFilter === 'all') return true;
    return job.status === jobStatusFilter;
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div 
          className="max-w-md w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              ADMIN ACCESS
            </h1>
            <p className="text-muted-foreground">
              Enter your admin credentials to continue
            </p>
          </div>

          <form onSubmit={handleLogin} className="bg-card border border-border rounded-sm p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin_token">Admin Secret Key</Label>
                <Input
                  id="admin_token"
                  type="password"
                  placeholder="Enter admin secret"
                  value={adminToken}
                  onChange={(e) => setAdminToken(e.target.value)}
                  data-testid="admin-token-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-active"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Access Admin Panel'
                )}
              </Button>
            </div>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-4">
            Contact system administrator for credentials
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                THM ADMIN
              </span>
            </Link>
            <Badge variant="outline" className="ml-4">Management</Badge>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => {
              localStorage.removeItem('thm_admin_token');
              setIsAuthenticated(false);
              setAdminToken('');
            }}
          >
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingProviders.length}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {providers.filter(p => p.verification_status === 'verified').length}
                </p>
                <p className="text-xs text-muted-foreground">Verified</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/10 rounded-sm flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {providers.filter(p => p.verification_status === 'rejected').length}
                </p>
                <p className="text-xs text-muted-foreground">Rejected</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{providers.length}</p>
                <p className="text-xs text-muted-foreground">Total Providers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Moderation Section */}
        {flaggedContent.length > 0 && (
          <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-sm p-6 mb-8" data-testid="content-moderation-section">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/10 rounded-sm flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                    CONTENT MODERATION
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Review flagged questions and answers that may contain contact information
                  </p>
                </div>
              </div>
              <Badge className="bg-yellow-500/15 text-yellow-600 text-lg px-3 py-1">
                {flaggedContent.length} Pending
              </Badge>
            </div>

            <div className="space-y-4">
              {flaggedContent.map((item) => (
                <div key={item.id} className="bg-card border border-border rounded-sm p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium">{item.provider_name}</p>
                      <p className="text-xs text-muted-foreground">Job: {item.job_title || item.job_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(item.created_at).toLocaleString('en-AU')}
                      </p>
                    </div>
                    {item.flagged_reasons?.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {item.flagged_reasons.map((reason, i) => (
                          <Badge key={i} variant="destructive" className="text-xs">
                            {reason.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Flagged Question */}
                  {item.moderation_status === 'pending_review' && (
                    <div className="mb-4 p-3 bg-yellow-500/10 rounded border border-yellow-500/20">
                      <p className="text-xs text-yellow-600 mb-1 font-medium">Flagged Question:</p>
                      <p className="text-sm mb-3">{item.question}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-green-600 border-green-500/30"
                          onClick={() => handleApproveQuestion(item.id)}
                          disabled={processingModeration === item.id}
                        >
                          {processingModeration === item.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3 h-3" />
                          )}
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-red-600 border-red-500/30"
                          onClick={() => handleDeleteQuestion(item.id)}
                          disabled={processingModeration === item.id}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Flagged Answer */}
                  {item.answer_moderation_status === 'pending_review' && item.answer && (
                    <div className="p-3 bg-yellow-500/10 rounded border border-yellow-500/20">
                      <p className="text-xs text-yellow-600 mb-1 font-medium">Flagged Answer:</p>
                      <p className="text-sm mb-3">{item.answer}</p>
                      {item.answer_flagged_reasons?.length > 0 && (
                        <div className="flex gap-1 flex-wrap mb-3">
                          {item.answer_flagged_reasons.map((reason, i) => (
                            <Badge key={i} variant="destructive" className="text-xs">
                              {reason.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-green-600 border-green-500/30"
                          onClick={() => handleApproveAnswer(item.id)}
                          disabled={processingModeration === `answer-${item.id}`}
                        >
                          {processingModeration === `answer-${item.id}` ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3 h-3" />
                          )}
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-red-600 border-red-500/30"
                          onClick={() => handleDeleteAnswer(item.id)}
                          disabled={processingModeration === `answer-${item.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Platform Fee Settings */}
        <div className="bg-card border border-border rounded-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <Percent className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                  PLATFORM FEE SETTINGS
                </h2>
                <p className="text-sm text-muted-foreground">
                  Adjust the platform fee deducted from provider payouts
                </p>
              </div>
            </div>
            <Badge className="bg-primary/15 text-primary text-lg px-3 py-1">
              Current: {platformFee}%
            </Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Fee Adjustment */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>New Platform Fee</Label>
                  <span className="text-2xl font-bold text-primary">{newPlatformFee}%</span>
                </div>
                <Slider
                  value={[newPlatformFee]}
                  onValueChange={(value) => setNewPlatformFee(value[0])}
                  max={50}
                  min={0}
                  step={0.5}
                  className="py-4"
                  data-testid="platform-fee-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0% (Promo)</span>
                  <span>10% (Default)</span>
                  <span>50% (Max)</span>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-muted/50 rounded-sm p-4 space-y-2">
                <p className="text-sm font-medium">Preview for A$100 job:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Provider receives:</span>
                  <span className="font-medium text-green-500">A${(100 - newPlatformFee).toFixed(2)}</span>
                  <span className="text-muted-foreground">Platform fee:</span>
                  <span className="font-medium text-primary">A${newPlatformFee.toFixed(2)}</span>
                  <span className="text-muted-foreground">Customer pays:</span>
                  <span className="font-medium">A$100.00</span>
                </div>
              </div>

              <Button 
                onClick={handleUpdatePlatformFee}
                disabled={updatingFee || newPlatformFee === platformFee}
                className="w-full btn-active gap-2"
                data-testid="update-fee-btn"
              >
                {updatingFee ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {newPlatformFee === platformFee ? 'No Changes' : `Update to ${newPlatformFee}%`}
              </Button>

              {newPlatformFee === 0 && (
                <p className="text-xs text-yellow-600 bg-yellow-500/10 p-2 rounded-sm">
                  Setting fee to 0% means providers receive 100% of payments. Use for promotions only!
                </p>
              )}
            </div>

            {/* Fee History */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <History className="w-4 h-4 text-muted-foreground" />
                <Label>Recent Changes</Label>
              </div>
              
              {feeHistory.length === 0 ? (
                <div className="bg-muted/50 rounded-sm p-4 text-center">
                  <p className="text-sm text-muted-foreground">No changes yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {feeHistory.map((change, index) => (
                    <div 
                      key={index} 
                      className="bg-muted/50 rounded-sm p-3 text-sm flex items-center justify-between"
                    >
                      <div>
                        <span className="text-muted-foreground">{change.old_value}%</span>
                        <span className="mx-2">→</span>
                        <span className="font-medium">{change.new_value}%</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(change.changed_at).toLocaleDateString('en-AU')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Job Management */}
        <div className="bg-card border border-border rounded-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                  JOB MANAGEMENT
                </h2>
                <p className="text-sm text-muted-foreground">
                  View, cancel, or delete jobs from the platform
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setShowOldJobs(!showOldJobs);
                  setTimeout(verifyAndFetch, 100);
                }}
              >
                {showOldJobs ? 'Hide Old Jobs' : 'Show All Jobs'}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleCleanupExpiredJobs}
                disabled={cleaningUp}
                className="gap-2"
              >
                {cleaningUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Cleanup Old Jobs
              </Button>
            </div>
          </div>

          {/* Job Filters */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {['all', 'posted', 'quoted', 'accepted', 'in_progress', 'completed', 'cancelled'].map((status) => (
              <Button
                key={status}
                variant={jobStatusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setJobStatusFilter(status)}
                className="capitalize"
              >
                {status.replace('_', ' ')}
                {status !== 'all' && (
                  <span className="ml-1">({jobs.filter(j => j.status === status).length})</span>
                )}
              </Button>
            ))}
          </div>

          {/* Jobs List */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {filteredJobs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No jobs found
              </div>
            ) : (
              filteredJobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{job.title}</span>
                      <Badge className={jobStatusColors[job.status] || 'bg-gray-500/15'}>
                        {job.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {job.pickup_city} → {job.delivery_city}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        {job.weight_kg}kg
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {job.quote_count || 0} quotes
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(job.created_at).toLocaleDateString('en-AU')}
                      </span>
                    </div>
                    {job.customer && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Customer: {job.customer.full_name} ({job.customer.email})
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchJobDetails(job)}
                      className="gap-1"
                      data-testid={`view-job-${job.id}`}
                    >
                      <Eye className="w-3 h-3" />
                      View Details
                    </Button>
                    {!['completed', 'cancelled', 'in_progress', 'picked_up'].includes(job.status) && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCancelJobDialog({ open: true, job })}
                          className="gap-1"
                        >
                          <Ban className="w-3 h-3" />
                          Cancel
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteJob(job.id)}
                          className="gap-1 text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Registration Analytics */}
        <div className="bg-card border border-border rounded-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                  REGISTRATION ANALYTICS
                </h2>
                <p className="text-sm text-muted-foreground">
                  Track signup funnel and abandoned registrations
                </p>
              </div>
            </div>
          </div>

          {registrationStats && (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-muted/30 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold">{registrationStats.total_providers}</p>
                  <p className="text-xs text-muted-foreground">Total Signups</p>
                </div>
                <div className="bg-green-500/10 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{registrationStats.completed_registrations}</p>
                  <p className="text-xs text-muted-foreground">Completed (Card Added)</p>
                </div>
                <div className="bg-red-500/10 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-red-500">{registrationStats.incomplete_registrations}</p>
                  <p className="text-xs text-muted-foreground">Abandoned at Card</p>
                </div>
                <div className="bg-purple-500/10 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-purple-500">{registrationStats.active_trials}</p>
                  <p className="text-xs text-muted-foreground">Active Trials</p>
                </div>
                <div className="bg-blue-500/10 rounded-sm p-4 text-center">
                  <p className="text-3xl font-bold text-blue-500">{registrationStats.active_subscribers}</p>
                  <p className="text-xs text-muted-foreground">Paid Subscribers</p>
                </div>
              </div>

              {/* Conversion Rate */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-muted/30 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Overall Conversion Rate</span>
                    <span className={`text-2xl font-bold ${registrationStats.conversion_rate > 50 ? 'text-green-500' : registrationStats.conversion_rate > 20 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {registrationStats.conversion_rate}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${registrationStats.conversion_rate > 50 ? 'bg-green-500' : registrationStats.conversion_rate > 20 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(registrationStats.conversion_rate, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {registrationStats.completed_registrations} of {registrationStats.total_providers} completed card setup
                  </p>
                </div>

                <div className="bg-muted/30 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Last 7 Days</span>
                    <span className={`text-2xl font-bold ${registrationStats.last_7_days.conversion_rate > 50 ? 'text-green-500' : 'text-yellow-500'}`}>
                      {registrationStats.last_7_days.conversion_rate}%
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">Signups</p>
                      <p className="font-medium">{registrationStats.last_7_days.signups}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Completed</p>
                      <p className="font-medium text-green-500">{registrationStats.last_7_days.completed}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Abandoned</p>
                      <p className="font-medium text-red-500">{registrationStats.last_7_days.abandoned}</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Incomplete Registrations List */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <UserX className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold">Abandoned Registrations ({incompleteRegistrations.length})</h3>
            </div>
            
            {incompleteRegistrations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p>No abandoned registrations!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3">Email</th>
                      <th className="text-left py-2 px-3">Company</th>
                      <th className="text-left py-2 px-3">Phone</th>
                      <th className="text-left py-2 px-3">Signup Date</th>
                      <th className="text-left py-2 px-3">Time Since</th>
                      <th className="text-left py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incompleteRegistrations.slice(0, 10).map((reg) => (
                      <tr key={reg.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                            {reg.email}
                          </div>
                        </td>
                        <td className="py-2 px-3">{reg.company_name || '-'}</td>
                        <td className="py-2 px-3">{reg.phone}</td>
                        <td className="py-2 px-3">
                          {new Date(reg.created_at).toLocaleDateString('en-AU')}
                        </td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className={reg.hours_since_signup > 24 ? 'text-red-500' : 'text-yellow-500'}>
                            {reg.days_since_signup > 0 
                              ? `${reg.days_since_signup} days ago`
                              : `${Math.round(reg.hours_since_signup || 0)} hours ago`
                            }
                          </Badge>
                        </td>
                        <td className="py-2 px-3">
                          <Badge className="bg-yellow-500/15 text-yellow-600">
                            <CreditCard className="w-3 h-3 mr-1" />
                            Card Pending
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {incompleteRegistrations.length > 10 && (
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Showing 10 of {incompleteRegistrations.length} abandoned registrations
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Banned Users Section */}
          {bannedUsers.length > 0 && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center gap-2 mb-4">
                <Ban className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold">Banned Users ({bannedUsers.length})</h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3">Email</th>
                      <th className="text-left py-2 px-3">Phone</th>
                      <th className="text-left py-2 px-3">Role</th>
                      <th className="text-left py-2 px-3">Banned Date</th>
                      <th className="text-left py-2 px-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bannedUsers.map((banned) => (
                      <tr key={banned.id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                            {banned.email}
                          </div>
                        </td>
                        <td className="py-2 px-3">{banned.phone || '-'}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline">{banned.original_role}</Badge>
                        </td>
                        <td className="py-2 px-3">
                          {new Date(banned.banned_at).toLocaleDateString('en-AU')}
                        </td>
                        <td className="py-2 px-3">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                            onClick={() => handleUnbanUser(banned)}
                            disabled={unbanningUser === banned.id}
                          >
                            {unbanningUser === banned.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              'Unban'
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Provider Management */}
        <div className="bg-card border border-border rounded-sm">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
              PROVIDER VERIFICATION
            </h2>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="p-6">
            <TabsList className="mb-6">
              <TabsTrigger value="pending" className="gap-2">
                <Clock className="w-4 h-4" />
                Pending ({pendingProviders.length})
              </TabsTrigger>
              <TabsTrigger value="all" className="gap-2">
                <Users className="w-4 h-4" />
                All Providers ({providers.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              {pendingProviders.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">No pending verifications</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pendingProviders.map(provider => (
                    <ProviderCard 
                      key={provider.id} 
                      provider={provider} 
                      onView={handleViewProvider}
                      onGrantTrial={handleOpenTrialDialog}
                      onDelete={(p) => setDeleteUserDialog({ open: true, user: p })}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="all">
              {providers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No providers yet</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {providers.map(provider => (
                    <ProviderCard 
                      key={provider.id} 
                      provider={provider} 
                      onView={handleViewProvider}
                      onGrantTrial={handleOpenTrialDialog}
                      onDelete={(p) => setDeleteUserDialog({ open: true, user: p })}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Failed Payouts Section */}
        {failedPayouts.length > 0 && (
          <div className="bg-orange-500/5 border border-orange-500/30 rounded-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-orange-500" />
                <h2 className="text-xl font-bold text-orange-500" style={{ fontFamily: 'Barlow Condensed' }}>
                  FAILED PAYOUTS ({failedPayouts.length})
                </h2>
              </div>
              <Button
                onClick={handleRetryAllPayouts}
                disabled={retryingAll}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="retry-all-payouts-btn"
              >
                {retryingAll ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry All Payouts
                  </>
                )}
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              These jobs are completed but the provider payout failed. Retry when Stripe balance is available.
            </p>
            
            <div className="space-y-3">
              {failedPayouts.map(job => (
                <div key={job.id} className="bg-background border border-border rounded-sm p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{job.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        Provider: {job.provider?.full_name || 'Unknown'} | 
                        Amount: A${((job.payment?.amount || 0) * 0.9).toFixed(2)}
                      </p>
                      <p className="text-xs text-red-500 mt-1">
                        Error: {job.payout_error}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleRetryPayout(job.id)}
                      disabled={retryingPayout === job.id}
                      variant="outline"
                      className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                      data-testid={`retry-payout-${job.id}`}
                    >
                      {retryingPayout === job.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Retry
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Danger Zone */}
        <div className="bg-red-500/5 border border-red-500/30 rounded-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <h2 className="text-xl font-bold text-red-500" style={{ fontFamily: 'Barlow Condensed' }}>
              DANGER ZONE
            </h2>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            These actions are irreversible and will permanently delete data from the database.
          </p>
          
          <Button 
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-500/10"
            onClick={() => setResetDialog({ open: true })}
            data-testid="reset-all-data-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete All Users & Data
          </Button>
        </div>
      </main>

      {/* Reset All Data Dialog */}
      <Dialog open={resetDialog.open} onOpenChange={(open) => { setResetDialog({ open }); setResetConfirmText(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="w-5 h-5" />
              DELETE ALL DATA
            </DialogTitle>
            <DialogDescription>
              This will permanently delete ALL users, jobs, quotes, reviews, messages, and notifications.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
              <p className="text-sm text-red-400 font-medium">
                ⚠️ This action CANNOT be undone. All data will be permanently deleted.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Type <span className="font-bold text-red-500">DELETE ALL</span> to confirm:</Label>
              <Input
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="DELETE ALL"
                className="border-red-500/30"
                data-testid="reset-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetDialog({ open: false }); setResetConfirmText(''); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleResetAllData}
              disabled={resettingData || resetConfirmText !== 'DELETE ALL'}
              data-testid="confirm-reset-btn"
            >
              {resettingData ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Trial Dialog */}
      <Dialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Gift className="w-5 h-5 text-purple-500" />
              GRANT FREE TRIAL
            </DialogTitle>
            <DialogDescription>
              Give {selectedProvider?.full_name} free access to browse and quote on jobs
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Trial Duration</Label>
              <Select value={trialDays} onValueChange={setTrialDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days (Recommended)</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-muted/50 rounded-sm text-sm">
              <p className="text-muted-foreground">
                The provider will receive an email notification and can immediately start viewing and quoting on jobs.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTrialDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGrantTrial}
              disabled={processing}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Gift className="w-4 h-4 mr-2" />
              )}
              Grant {trialDays}-Day Trial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              PROVIDER DETAILS
            </DialogTitle>
            <DialogDescription>
              Review provider information and documents
            </DialogDescription>
          </DialogHeader>

          {selectedProvider && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium">{selectedProvider.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Company</p>
                  <p className="font-medium">{selectedProvider.company_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedProvider.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedProvider.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">ABN</p>
                  <p className="font-medium font-mono">{selectedProvider.abn || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={statusColors[selectedProvider.verification_status] || statusColors.pending}>
                    {selectedProvider.verification_status || 'pending'}
                  </Badge>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="font-semibold mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                  SUBMITTED DOCUMENTS
                </h4>
                <div className="space-y-2">
                  {selectedProvider.photo_id_url ? (
                    <a 
                      href={`${API_URL}${selectedProvider.photo_id_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-muted/50 rounded-sm hover:bg-muted transition-colors"
                    >
                      <FileText className="w-4 h-4 text-primary" />
                      <span>Photo ID</span>
                      <ExternalLink className="w-4 h-4 ml-auto" />
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-sm">
                      Photo ID not submitted
                    </p>
                  )}
                  
                  {selectedProvider.insurance_url ? (
                    <a 
                      href={`${API_URL}${selectedProvider.insurance_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-muted/50 rounded-sm hover:bg-muted transition-colors"
                    >
                      <FileText className="w-4 h-4 text-primary" />
                      <span>Insurance Certificate</span>
                      <ExternalLink className="w-4 h-4 ml-auto" />
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-sm">
                      Insurance not submitted
                    </p>
                  )}
                  
                  {selectedProvider.business_reg_url ? (
                    <a 
                      href={`${API_URL}${selectedProvider.business_reg_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-muted/50 rounded-sm hover:bg-muted transition-colors"
                    >
                      <FileText className="w-4 h-4 text-primary" />
                      <span>Business Registration</span>
                      <ExternalLink className="w-4 h-4 ml-auto" />
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-sm">
                      Business Registration not submitted (optional)
                    </p>
                  )}
                </div>
              </div>

              {/* Action Notes */}
              {selectedProvider.verification_status === 'under_review' && (
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add notes for the provider (will be shown if rejected)"
                    value={actionNotes}
                    onChange={(e) => setActionNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
            {selectedProvider?.verification_status === 'under_review' && (
              <>
                <Button 
                  variant="destructive"
                  onClick={() => handleVerifyAction('reject')}
                  disabled={processing}
                  data-testid="reject-provider-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Reject
                </Button>
                <Button 
                  className="btn-active"
                  onClick={() => handleVerifyAction('approve')}
                  disabled={processing}
                  data-testid="approve-provider-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Verify Provider
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Job Dialog */}
      <Dialog open={cancelJobDialog.open} onOpenChange={(open) => setCancelJobDialog({ open, job: cancelJobDialog.job })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Job</DialogTitle>
            <DialogDescription>
              This will cancel the job "{cancelJobDialog.job?.title}" and notify the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for cancellation (optional)</Label>
              <Textarea
                placeholder="e.g., Job details were incomplete, duplicate posting, etc."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelJobDialog({ open: false, job: null })}>
              Go Back
            </Button>
            <Button onClick={handleCancelJob} className="bg-red-500 hover:bg-red-600">
              Cancel Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteUserDialog.open} onOpenChange={(open) => !open && setDeleteUserDialog({ open: false, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl text-red-500" style={{ fontFamily: 'Barlow Condensed' }}>
              DELETE USER ACCOUNT
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>Are you sure you want to delete this user?</p>
              {deleteUserDialog.user && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-3">
                  <p><strong>Email:</strong> {deleteUserDialog.user.email}</p>
                  <p><strong>Name:</strong> {deleteUserDialog.user.full_name}</p>
                  <p><strong>Type:</strong> {deleteUserDialog.user.role}</p>
                  {deleteUserDialog.user.company_name && (
                    <p><strong>Company:</strong> {deleteUserDialog.user.company_name}</p>
                  )}
                </div>
              )}
              <p className="text-red-500 font-medium">
                This action cannot be undone. The user will be notified via email.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteUserDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deletingUser}
            >
              {deletingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Detail Modal - Admin View */}
      <Dialog open={jobDetailDialog.open} onOpenChange={(open) => !open && setJobDetailDialog({ open: false, job: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Job Details - Admin View
            </DialogTitle>
            <DialogDescription>
              Full job information including quotes, Q&A, and messages for dispute resolution
            </DialogDescription>
          </DialogHeader>

          {loadingJobDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : jobDetailDialog.job && (
            <div className="space-y-6">
              {/* Job Summary */}
              <div className="bg-muted/30 rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-lg">{jobDetailDialog.job.title}</h3>
                  <Badge className={jobStatusColors[jobDetailDialog.job.status] || 'bg-gray-500/15'}>
                    {jobDetailDialog.job.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{jobDetailDialog.job.description}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Pickup:</span>
                    <p className="font-medium">{jobDetailDialog.job.pickup_location}, {jobDetailDialog.job.pickup_city}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Delivery:</span>
                    <p className="font-medium">{jobDetailDialog.job.delivery_location}, {jobDetailDialog.job.delivery_city}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Load:</span>
                    <p className="font-medium">{jobDetailDialog.job.load_type} - {jobDetailDialog.job.weight_kg}kg</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Posted:</span>
                    <p className="font-medium">{new Date(jobDetailDialog.job.created_at).toLocaleString('en-AU')}</p>
                  </div>
                </div>
                {jobDetailDialog.job.customer && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <span className="text-muted-foreground text-sm">Customer:</span>
                    <p className="font-medium">{jobDetailDialog.job.customer.full_name}</p>
                    <p className="text-sm text-muted-foreground">{jobDetailDialog.job.customer.email} | {jobDetailDialog.job.customer.phone}</p>
                  </div>
                )}
              </div>

              {/* Quotes Section */}
              <div className="border border-border rounded-sm p-4">
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-primary" />
                  Quotes ({jobQuotes.length})
                </h4>
                {jobQuotes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No quotes submitted yet</p>
                ) : (
                  <div className="space-y-3">
                    {jobQuotes.map((quote) => (
                      <div key={quote.id} className={`p-3 rounded border ${quote.status === 'accepted' ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/30 border-border'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{quote.provider_name}</span>
                            {quote.provider_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                              {quote.provider_rating?.toFixed(1) || '0.0'}
                            </div>
                          </div>
                          <Badge className={quote.status === 'accepted' ? 'bg-green-500/15 text-green-600' : 'bg-gray-500/15'}>
                            {quote.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Price:</span>
                            <p className="font-bold text-primary">A${quote.price?.toFixed(2)}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Provider ID:</span>
                            <p className="text-xs font-mono">{quote.provider_id}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Submitted:</span>
                            <p className="text-xs">{new Date(quote.created_at).toLocaleString('en-AU')}</p>
                          </div>
                        </div>
                        {quote.notes && (
                          <div className="mt-2 text-sm">
                            <span className="text-muted-foreground">Notes:</span>
                            <p className="italic">"{quote.notes}"</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Q&A Section */}
              <div className="border border-border rounded-sm p-4">
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <HelpCircle className="w-4 h-4 text-primary" />
                  Questions & Answers ({jobQuestions.length})
                </h4>
                {jobQuestions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No questions asked</p>
                ) : (
                  <div className="space-y-3">
                    {jobQuestions.map((q) => (
                      <div key={q.id} className={`p-3 rounded border ${q.is_flagged ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-muted/30 border-border'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{q.provider_name}</span>
                          <span className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString('en-AU')}</span>
                          {q.is_flagged && <Badge className="bg-yellow-500/15 text-yellow-600 text-xs">Flagged</Badge>}
                          {q.moderation_status === 'pending_review' && <Badge className="bg-red-500/15 text-red-600 text-xs">Pending Review</Badge>}
                        </div>
                        <p className="text-sm mb-2"><strong>Q:</strong> {q.question}</p>
                        {q.answer && (
                          <div className={`ml-4 p-2 rounded ${q.answer_flagged ? 'bg-yellow-500/10' : 'bg-background'}`}>
                            <p className="text-sm"><strong>A:</strong> {q.answer}</p>
                            {q.answer_flagged && <Badge className="bg-yellow-500/15 text-yellow-600 text-xs mt-1">Answer Flagged</Badge>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Private Messages Section */}
              {jobMessages.length > 0 && (
                <div className="border border-border rounded-sm p-4">
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-primary" />
                    Private Messages ({jobMessages.length})
                  </h4>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {jobMessages.map((msg) => (
                      <div key={msg.id} className="p-2 bg-muted/30 rounded text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{msg.sender_name}</span>
                          <Badge variant="outline" className="text-xs">{msg.sender_role}</Badge>
                          <span className="text-xs text-muted-foreground">{new Date(msg.created_at).toLocaleString('en-AU')}</span>
                        </div>
                        <p>{msg.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setJobDetailDialog({ open: false, job: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
